import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  LoadingSkeleton,
} from '@nexora/design-system';

/* ── Contratos reais (backend/.review-loop/reports/served-routes.json) ──
 * GET /finance/payables/dashboard → PayableDashboardDto
 *   { cashFlow30d[CashFlowDayDto], projectedTotalInflow, projectedTotalOutflow,
 *     projectedNet, upcoming[UpcomingPayableItemDto], alerts[CashAlertDto],
 *     summary{ totalOpen, overdueTotal, dueNext7Days, currentCash, ... } }
 * (DTOs em backend/.../dto/treasury.dto.ts)
 */

interface CashFlowDay {
  date: string;
  inflow: number;
  outflow: number;
  net: number;
  projectedBalance: number;
}

interface UpcomingPayableItem {
  id: string;
  supplierName: string;
  documentNumber: string;
  dueDate: string;
  value: number;
  balance: number;
  status: string;
  category: string;
  daysToDue: number;
}

interface CashAlert {
  severity: 'CRITICAL' | 'WARNING' | 'OK';
  message: string;
  value: number;
}

interface PayableDashboard {
  cashFlow30d: CashFlowDay[];
  projectedTotalInflow: number;
  projectedTotalOutflow: number;
  projectedNet: number;
  upcoming: UpcomingPayableItem[];
  alerts: CashAlert[];
  summary: {
    totalOpen: number;
    totalOpenCount: number;
    overdueTotal: number;
    overdueCount: number;
    dueNext7Days: number;
    dueNext7DaysCount: number;
    currentCash: number;
  };
}

function statusBadge(status: string) {
  const map: Record<string, { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }> = {
    OPEN: { variant: 'info', label: 'Em aberto' },
    PARTIALLY_SETTLED: { variant: 'warning', label: 'Parcial' },
    SETTLED: { variant: 'success', label: 'Liquidado' },
    OVERDUE: { variant: 'error', label: 'Vencido' },
    RENEGOTIATED: { variant: 'warning', label: 'Renegociado' },
    CANCELLED: { variant: 'neutral', label: 'Cancelado' },
    SCHEDULED: { variant: 'info', label: 'Agendado' },
  };
  return map[status] ?? { variant: 'neutral' as const, label: status };
}

function severityBadge(severity: CashAlert['severity']) {
  const map: Record<CashAlert['severity'], { variant: 'error' | 'warning' | 'success'; label: string }> = {
    CRITICAL: { variant: 'error', label: 'Crítico' },
    WARNING: { variant: 'warning', label: 'Atenção' },
    OK: { variant: 'success', label: 'Ok' },
  };
  return map[severity];
}

/** Gráfico de barras entradas×saídas por dia — SVG inline, padrão do dashboard financeiro. */
function FlowChart({ days }: { days: CashFlowDay[] }): JSX.Element {
  const W = 720;
  const H = 220;
  const padL = 88;
  const padR = 12;
  const padB = 24;
  const padT = 16;
  const max = Math.max(1, ...days.map((d) => Math.max(d.inflow, d.outflow)));
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const slot = innerW / Math.max(1, days.length);
  const barW = Math.min(14, Math.max(6, slot / 3.2));

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-auto"
      role="img"
      aria-label={`Fluxo de caixa projetado: entradas ${formatCurrency(days.reduce((a, d) => a + d.inflow, 0))} e saídas ${formatCurrency(days.reduce((a, d) => a + d.outflow, 0))} em ${days.length} dias.`}
    >
      {[0.25, 0.5, 0.75, 1].map((f) => {
        const y = padT + innerH * (1 - f);
        return (
          <g key={f}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#e0e3e5" strokeWidth={1} />
            <text x={padL - 6} y={y - 4} textAnchor="end" fontSize={10} fill="#74777f">
              {formatCurrency(max * f)}
            </text>
          </g>
        );
      })}
      {days.map((d, i) => {
        const cx = padL + slot * i + slot / 2;
        const hi = (d.inflow / max) * innerH;
        const ho = (d.outflow / max) * innerH;
        return (
          <g key={d.date}>
            <title>{`${formatDate(d.date)}: entradas ${formatCurrency(d.inflow)} · saídas ${formatCurrency(d.outflow)} · líquido ${formatCurrency(d.net)}`}</title>
            <rect x={cx - barW - 2} y={padT + innerH - hi} width={barW} height={Math.max(2, hi)} rx={3} fill="#2e7d32" />
            <rect x={cx + 2} y={padT + innerH - ho} width={barW} height={Math.max(2, ho)} rx={3} fill="#ba1a1a" />
            <text x={cx} y={H - 6} textAnchor="middle" fontSize={9} fill="#74777f">
              {i % 5 === 0 ? formatDate(d.date).slice(0, 5) : ''}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function PayablesDashboardPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';

  const dashboardQ = useQuery<PayableDashboard>({
    queryKey: queryKeys.finance.dashboard(tenantId).concat('payables' as const),
    queryFn: async () => apiClient.get<PayableDashboard>('/finance/payables/dashboard'),
    enabled: !!tenantId,
  });

  const data = dashboardQ.data;
  const days = useMemo(() => data?.cashFlow30d ?? [], [data]);

  return (
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Dashboard de Contas a Pagar</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Visão geral financeira e projeções para os próximos 30 dias.
          </p>
        </div>
      </div>

      {dashboardQ.isLoading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
          <LoadingSkeleton />
          <span style={{ fontSize: '0.875rem' }}>Carregando dashboard de contas a pagar...</span>
        </div>
      )}

      {dashboardQ.error && !dashboardQ.isLoading && (
        <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
          <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar dashboard</p>
          <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>{(dashboardQ.error as Error).message}</p>
        </div>
      )}

      {!dashboardQ.isLoading && !dashboardQ.error && data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-md mb-lg">
            <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
              <span className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider">Total a pagar</span>
              <p className="font-display-lg text-display-lg text-on-surface m-0 mt-sm tabular-nums">{formatCurrency(data.summary.totalOpen)}</p>
              <p className="font-body-sm text-body-sm text-on-surface-variant mt-xs">{data.summary.totalOpenCount} títulos em aberto</p>
            </div>
            <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
              <span className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider">Vencido</span>
              <p className="font-display-lg text-display-lg text-error m-0 mt-sm tabular-nums">{formatCurrency(data.summary.overdueTotal)}</p>
              <p className="font-body-sm text-body-sm text-on-surface-variant mt-xs">{data.summary.overdueCount} títulos vencidos</p>
            </div>
            <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
              <span className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider">Vence em 7 dias</span>
              <p className="font-display-lg text-display-lg text-warning-amber m-0 mt-sm tabular-nums">{formatCurrency(data.summary.dueNext7Days)}</p>
              <p className="font-body-sm text-body-sm text-on-surface-variant mt-xs">{data.summary.dueNext7DaysCount} títulos · caixa atual {formatCurrency(data.summary.currentCash)}</p>
            </div>
          </div>

          {/* Fluxo 30 dias */}
          <Card variant="outlined" padding="md" className="mb-lg">
            <CardHeader>
              <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Fluxo de Caixa Projetado — 30 dias</h3>
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                Entradas {formatCurrency(data.projectedTotalInflow)} · Saídas {formatCurrency(data.projectedTotalOutflow)} · Líquido {formatCurrency(data.projectedNet)}
              </span>
            </CardHeader>
            <CardBody>
              {days.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#74777f' }}>
                  Nenhum fluxo projetado no período.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <FlowChart days={days} />
                </div>
              )}
            </CardBody>
          </Card>

          {/* Próximos vencimentos + alertas */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-md">
            <Card variant="outlined" padding="0" className="lg:col-span-2">
              <CardHeader className="px-md pt-md">
                <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Próximos Vencimentos</h3>
              </CardHeader>
              {data.upcoming.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#74777f' }}>
                  Nenhum vencimento próximo.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr>
                        {['Fornecedor', 'Vencimento', 'Valor', 'Status / Categoria'].map((h) => (
                          <th
                            key={h}
                            style={{
                              textAlign: h === 'Valor' ? 'right' : 'left',
                              padding: '0.75rem 1rem',
                              background: '#f2f4f6',
                              color: '#43474e',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              borderBottom: '2px solid #e0e3e5',
                              whiteSpace: 'nowrap',
                              position: 'sticky',
                              top: 0,
                              zIndex: 1,
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.upcoming.map((u) => (
                        <tr key={u.id} style={{ transition: 'background 0.15s ease' }}>
                          <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 500 }}>
                            {u.supplierName}
                            <span style={{ display: 'block', fontSize: '12px', color: '#74777f' }}>NF: {u.documentNumber}</span>
                          </td>
                          <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                            {formatDate(u.dueDate)}
                            {u.daysToDue <= 0 ? (
                              <Badge variant="error" size="sm" dot>venceu</Badge>
                            ) : u.daysToDue <= 7 ? (
                              <Badge variant="warning" size="sm" dot>{u.daysToDue}d</Badge>
                            ) : null}
                          </td>
                          <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 600, textAlign: 'right', fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontVariantNumeric: 'tabular-nums' }}>
                            {formatCurrency(u.balance)}
                          </td>
                          <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                            <Badge variant={statusBadge(u.status).variant} size="sm" dot>{statusBadge(u.status).label}</Badge>
                            {u.category && <span style={{ fontSize: '12px', color: '#74777f', marginLeft: '0.5rem' }}>{u.category}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card variant="outlined" padding="0">
              <CardHeader className="px-md pt-md">
                <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Alertas de Caixa</h3>
              </CardHeader>
              {data.alerts.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#74777f' }}>
                  Nenhum alerta de caixa no momento.
                </div>
              ) : (
                <div className="flex flex-col gap-sm p-md">
                  {data.alerts.map((a, i) => (
                    <div
                      key={`${a.message}-${i}`}
                      className="flex items-start gap-sm p-sm rounded border border-outline-variant bg-surface-container-lowest"
                    >
                      <Badge variant={severityBadge(a.severity).variant} size="sm" dot>{severityBadge(a.severity).label}</Badge>
                      <div className="flex-1">
                        <p className="font-body-sm text-body-sm text-on-surface m-0">{a.message}</p>
                        <p className="font-label-md text-label-md text-on-surface-variant m-0 tabular-nums">{formatCurrency(a.value)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}